"use client";

import React, { useEffect, useRef, useState } from "react";

interface LazyBackgroundProps {
  backgroundUrl: string;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

// Lazy applies background-image once in-viewport to avoid early network work
export function LazyBackground({ backgroundUrl, className, style, children }: LazyBackgroundProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // If IntersectionObserver isn't available, degrade gracefully
    if (!("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        });
      },
      { root: null, rootMargin: "0px 0px 200px 0px", threshold: 0.01 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const mergedStyle: React.CSSProperties = {
    ...(style || {}),
    ...(inView
      ? {
          backgroundImage: `url(${backgroundUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "left top",
          backgroundRepeat: "no-repeat",
        }
      : {}),
  };

  return (
    <div ref={ref} className={className} style={mergedStyle}>
      {children}
    </div>
  );
}
